import {
  ArrowRight,
  CheckCircle2,
  FolderTree,
  LockKeyhole,
  Search,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/common/logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const features = [
  {
    icon: FolderTree,
    title: "Documentation that stays organized",
    description:
      "Shape ideas into pages and nested subpages that grow with every project.",
  },
  {
    icon: UsersRound,
    title: "Built for clear collaboration",
    description:
      "Invite the right people, assign simple roles, and keep everyone aligned.",
  },
  {
    icon: LockKeyhole,
    title: "Control access page by page",
    description:
      "Keep sensitive decisions private without splitting your knowledge base.",
  },
  {
    icon: Search,
    title: "Find context in a moment",
    description:
      "Search across everything you can access, with favorites and recents close by.",
  },
];

export function LandingPage() {
  return (
    <div className="min-h-screen overflow-hidden bg-white text-slate-950">
      <header className="relative z-10 mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex" aria-label="Public">
          <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-950">
            Features
          </a>
          <a href="#security" className="text-sm font-medium text-slate-600 hover:text-slate-950">
            Security
          </a>
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-indigo-700">
            Sign in
          </Link>
        </nav>
        <Button asChild size="sm">
          <Link to="/login">Get started</Link>
        </Button>
      </header>

      <main>
        <section className="relative px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
          <div className="absolute inset-x-0 top-0 -z-10 h-[620px] bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.16),transparent_58%)]" />
          <div className="mx-auto max-w-4xl text-center">
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700">
              <Sparkles className="size-3.5" />
              One calm place for your team&apos;s knowledge
            </div>
            <h1 className="text-balance text-5xl font-extrabold tracking-[-0.045em] text-slate-950 sm:text-7xl">
              Turn scattered ideas into{" "}
              <span className="bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
                organized knowledge.
              </span>
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-pretty text-lg leading-8 text-slate-600 sm:text-xl">
              Create structured documentation, collaborate with your team, and
              control exactly who can view or edit every page.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/login">
                  Continue with Google <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#features">Explore features</a>
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-400">
              No password to remember. Sign in securely with Google.
            </p>
          </div>

          <div className="mx-auto mt-20 max-w-6xl rounded-[28px] border border-slate-200 bg-slate-950 p-2 shadow-2xl shadow-indigo-200/60 sm:p-3">
            <div className="grid min-h-[480px] overflow-hidden rounded-2xl bg-slate-50 md:grid-cols-[220px_1fr]">
              <div className="hidden border-r border-slate-200 bg-slate-100 p-4 text-left md:block">
                <Logo className="scale-90 origin-left" />
                <div className="mt-7 space-y-2">
                  {["Home", "Search", "Favorites", "Recent"].map((item, index) => (
                    <div
                      key={item}
                      className={`rounded-lg px-3 py-2 text-sm ${
                        index === 0
                          ? "bg-white font-semibold text-indigo-700 shadow-sm"
                          : "text-slate-500"
                      }`}
                    >
                      {item}
                    </div>
                  ))}
                </div>
                <p className="mt-8 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Product launch
                </p>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <div className="rounded-lg bg-white px-3 py-2">🚀 Launch plan</div>
                  <div className="px-5 py-2">↳ Positioning</div>
                  <div className="px-5 py-2">↳ Release checklist</div>
                </div>
              </div>
              <div className="p-7 text-left sm:p-12">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  Product launch <span>/</span> Launch plan
                </div>
                <div className="mt-12 text-5xl">🚀</div>
                <h2 className="mt-5 text-4xl font-bold tracking-tight">
                  Launch plan
                </h2>
                <p className="mt-3 max-w-2xl leading-7 text-slate-500">
                  Everything our team needs to ship with clarity and confidence.
                </p>
                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                  {[
                    ["Positioning", "Align the story we tell"],
                    ["Release checklist", "Track every launch detail"],
                    ["Customer FAQ", "Answer questions before they arise"],
                    ["Success metrics", "Know what good looks like"],
                  ].map(([title, description]) => (
                    <Card key={title} className="p-5">
                      <CheckCircle2 className="size-5 text-emerald-500" />
                      <h3 className="mt-4 font-semibold">{title}</h3>
                      <p className="mt-1 text-sm text-slate-500">{description}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="border-y border-slate-100 bg-slate-50 px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-widest text-indigo-600">
                Clarity by default
              </p>
              <h2 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                A focused workspace for work that matters.
              </h2>
            </div>
            <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {features.map(({ icon: Icon, title, description }) => (
                <Card key={title} className="p-6">
                  <span className="grid size-11 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Icon className="size-5" />
                  </span>
                  <h3 className="mt-6 text-lg font-bold">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="security" className="px-5 py-24 sm:px-8">
          <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
            <div>
              <span className="grid size-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
                <LockKeyhole className="size-6" />
              </span>
              <h2 className="mt-6 text-4xl font-bold tracking-tight">
                Your knowledge stays with the right people.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                Group roles and page-level controls work together so private
                context never leaks through search, links, or navigation.
              </p>
            </div>
            <div className="rounded-3xl bg-slate-950 p-8 text-white shadow-2xl">
              {[
                "Google-only identity",
                "Private and invite-only groups",
                "Role-based editing",
                "Page-level access controls",
                "Secure file access",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 border-b border-white/10 py-4 last:border-0">
                  <CheckCircle2 className="size-5 text-emerald-400" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 pb-24 sm:px-8">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-indigo-600 px-7 py-16 text-center text-white sm:px-14">
            <h2 className="text-4xl font-bold tracking-tight">
              Give your best ideas a place to grow.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-indigo-100">
              Organize knowledge. Collaborate clearly. Start your first workspace today.
            </p>
            <Button asChild size="lg" className="mt-8 bg-white text-indigo-700 hover:bg-indigo-50">
              <Link to="/login">Continue with Google</Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 px-5 py-8 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 text-sm text-slate-500 sm:flex-row">
          <Logo />
          <p>© {new Date().getFullYear()} Master Notes. Organize knowledge. Collaborate clearly.</p>
        </div>
      </footer>
    </div>
  );
}
